import { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Container,
  Card,
  Spinner,
  Badge,
} from "react-bootstrap";
import { FaHistory } from "react-icons/fa";
import moment from "moment";
import axios from "axios";
import { BASE_URL, HEADERS } from "../../../utils/constant";

const AmcViewModel = ({ show, handleClose, amcId, amc }) => {
  const [amcLogs, setAmcLogs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !amcId) return;

    const fetchAmcLogs = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${BASE_URL}/api/amc-logs/${amcId}/`,
          { headers: HEADERS }
        );
        setAmcLogs(response.data);
      } catch (err) {
        console.error("Error fetching AMC logs:", err);
        setError("Failed to load AMC activity.");
      } finally {
        setLoading(false);
      }
    };

    fetchAmcLogs();
  }, [show, amcId]);

  // ✅ STATUS COLOR
  const getBadgeColor = (status) => {
    switch (status) {
      case "Upcoming":
        return "#0dcaf0";
      case "Renewal":
        return "#ffc107";
      case "Completed":
        return "#28a745";
      case "Expired":
        return "#dc3545";
      default:
        return "#343a40";
    }
  };

  // ✅ RENDER FIELD CHANGES
  const renderChanges = (changes) => {
    if (!changes || typeof changes !== "object") return "—";

    const validChanges = Object.entries(changes).filter(
      ([, v]) => v?.new !== null && v?.new !== "" && v?.new !== undefined
    );

    if (validChanges.length === 0) return "—";

    return (
      <ul className="mb-0 ps-3">
        {validChanges.map(([field, value], idx) => (
          <li key={idx}>
            <strong>{field}</strong>:{" "}
            <span className="text-muted">{value.previous ?? "—"}</span> →{" "}
            <span className="text-success">{value.new}</span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      scrollable
      dialogClassName="custom-modern-modal"
    >
      {/* ---------------- HEADER ---------------- */}
      <Modal.Header closeButton>
        <Modal.Title className="w-100 d-flex justify-content-between align-items-center">
          <span className="fw-semibold">
            📄 {amc?.amcName || "AMC Details"}
          </span>

          <span
            className="badge"
            style={{
              backgroundColor: getBadgeColor(amc?.status),
              padding: "6px 12px",
              color: "#fff",
            }}
          >
            {amc?.status}
          </span>
        </Modal.Title>
      </Modal.Header>

      {/* ---------------- BODY ---------------- */}
      <Modal.Body className="p-4">
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" />
            <p className="mt-2">Loading AMC data...</p>
          </div>
        ) : error ? (
          <p className="text-danger text-center">{error}</p>
        ) : (
          <Container fluid>
            {/* -------- BASIC DETAILS CARD -------- */}
            <Card className="mb-3 shadow-sm">
              <Card.Body className="small">
                <Row>
                  <Col md={6}>
                    {amc?.created_at && (
                      <p className="mb-1">
                        <strong>Created At:</strong>{" "}
                        {moment(amc.created_at).format("DD-MM-YYYY")}
                      </p>
                    )}

                    {amc?.created_by && (
                      <p className="mb-1">
                        <strong>Created By:</strong> {amc.created_by}
                      </p>
                    )}

                    {amc?.customer_name && (
                      <p className="mb-1">
                        <strong>Customer:</strong> {amc.customer_name}
                      </p>
                    )}

                    {amc?.service_name && (
                      <p className="mb-1">
                        <strong>Service:</strong> {amc.service_name}
                      </p>
                    )}
                  </Col>

                  <Col md={6}>
                    {amc?.start_date && (
                      <p className="mb-1">
                        <strong>Start Date:</strong>{" "}
                        {moment(amc.start_date).format("DD-MM-YYYY")}
                      </p>
                    )}

                    {amc?.end_date && (
                      <p className="mb-1">
                        <strong>End Date:</strong>{" "}
                        {moment(amc.end_date).format("DD-MM-YYYY")}
                      </p>
                    )}

                    {amc?.amount && (
                      <p className="mb-1">
                        <strong>Amount:</strong> ₹ {amc.amount}
                      </p>
                    )}

                    {amc?.status && (
                      <p className="mb-1">
                        <strong>Status:</strong> {amc.status}
                      </p>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* -------- ACTIVITY LOG CARD -------- */}
            <Card className="shadow-sm">
              <Card.Body>
                <h5 className="text-primary mb-3">
                  <FaHistory className="me-2" />
                  AMC Activity Log
                </h5>

                {amcLogs?.logs?.length > 0 ? (
                  <div style={{ overflowX: "auto" }}>
                    <table className="inventory-table w-100">
                      <thead>
                        <tr>
                          <th style={{ minWidth: "150px" }}>Updated By</th>
                          <th style={{ minWidth: "170px" }}>Date & Time</th>
                          <th style={{ minWidth: "120px" }}>Action</th>
                          <th style={{ minWidth: "250px" }}>Changes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {amcLogs.logs.map((log, idx) => (
                          <tr key={idx}>
                            <td>{log.updated_by || "—"}</td>
                            <td>
                              {moment(log.updated_on).format(
                                "DD-MM-YYYY hh:mm A"
                              )}
                            </td>
                            <td>
                              <Badge bg="secondary">
                                {log.action || "—"}
                              </Badge>
                            </td>
                            <td>{renderChanges(log.changes)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted text-center mb-0">
                    No AMC activity available.
                  </p>
                )}
              </Card.Body>
            </Card>
          </Container>
        )}
      </Modal.Body>

      {/* ---------------- FOOTER ---------------- */}
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AmcViewModel;